import React from "react";
import SEO from "@/lib/seo-static.json";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { ROLES, SITE } from "@/lib/site";
import { getModule, moduleHref } from "@/lib/modules";
import { Container, Section, Reveal, Kicker, CTAButton, usePageTitle } from "@/components/ui-kit";
import { RolesSection } from "@/components/sections/Narrative";
import { Testimonials, CTABand } from "@/components/sections/Social";

export default function Solutions() {
  usePageTitle(SEO["/solutions"].title, SEO["/solutions"].description, SEO["/solutions"].image);
  return (
    <div data-testid="solutions-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-60" aria-hidden />
        <Container className="relative pt-14 pb-12 lg:pt-20">
          <Kicker>By role</Kicker>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-mk-text leading-[1] text-balance max-w-4xl">Whatever hat you wear, Fixlr is already shaped for it.</h1>
          <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed max-w-2xl">Same engine, different front door. Pick your role to see the modules that matter most and how teams like yours run their day.</p>
        </Container>
        <Container className="pb-8"><RolesSection compact /></Container>
      </section>

      {ROLES.map((r, i) => (
        <Section key={r.slug} id={r.slug} className={`scroll-mt-24 ${i % 2 ? "" : "bg-mk-surface/40 border-y border-mk-border"}`} data-testid={`solution-${r.slug}`}>
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <Reveal className={`lg:col-span-5 min-w-0 ${i % 2 ? "lg:order-2" : ""}`}>
                <div className="relative overflow-hidden rounded-3xl border border-mk-border h-[380px]"><img src={r.image} alt={r.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-70" /><div className="absolute inset-0 bg-gradient-to-t from-mk-bg/90 via-transparent to-transparent" /><div className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs text-mk-text"><r.icon className="h-3.5 w-3.5 text-mk-lime" />{r.short}</div></div>
              </Reveal>
              <div className={`lg:col-span-7 min-w-0 ${i % 2 ? "lg:order-1" : ""}`}>
                <div className="eyebrow">{r.title}</div>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-mk-text text-balance">{r.headline}</h2>
                <p className="mt-4 text-mk-dim leading-relaxed">{r.desc}</p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">{r.bullets.map((b) => <li key={b} className="flex items-start gap-2.5 text-sm text-mk-text/90"><Check className="h-4 w-4 text-mk-lime shrink-0 mt-0.5" />{b}</li>)}</ul>
                <div className="mt-6">
                  <div className="text-xs font-semibold uppercase tracking-wider text-mk-muted">Modules they lean on</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {r.modules.map(getModule).filter(Boolean).map((m) => <Link key={m.slug} to={moduleHref(m)} data-testid={`solution-${r.slug}-module-${m.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-mk-border bg-mk-surface px-3 py-1.5 text-xs text-mk-dim hover:text-mk-text hover:border-mk-lime/50 transition-colors"><m.icon className="h-3.5 w-3.5 text-mk-lime" />{m.name}</Link>)}
                  </div>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <CTAButton href={SITE.signupUrl} testId={`solution-${r.slug}-trial`}>Start free trial <ArrowRight className="h-4 w-4" /></CTAButton>
                  <CTAButton to="/contact" variant="ghost" testId={`solution-${r.slug}-demo`}>Book a demo</CTAButton>
                </div>
              </div>
            </div>
          </Container>
        </Section>
      ))}

      <Section className="pt-0" data-testid="solutions-contractor-callout">
        <Container>
          <Reveal className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 rounded-2xl border border-mk-lime/30 bg-mk-lime/[0.04] p-6 sm:p-8">
            <div className="min-w-0"><div className="eyebrow">Contractors & tradespeople</div><h3 className="mt-2 font-display text-xl sm:text-2xl font-bold text-mk-text">Not running an FM team — looking for work from them?</h3><p className="mt-2 text-sm text-mk-dim">List your business on the Fixlr Marketplace for free. No commission; quote requests arrive with the job pack attached.</p></div>
            <Link to="/for-contractors" className="btn-lime shrink-0" data-testid="solutions-contractor-link">For contractors <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </Container>
      </Section>
      <Testimonials />
      <CTABand />
    </div>
  );
}
